import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import _, { concat, reduce, nth, filter, findKey, size } from 'lodash';
import { Terminal } from '../types/graph';
import { useCurrentUser } from '../hooks';
import styled from '../themes/themed-styled-components';
import { ItemType } from 'tsdef';

type TerminalSelectorProps = {
    id: number | null
}

const separatorItem = (key: string) => <div key={`${key}separator`} className="dropdown-divider"></div>;
const portItem = ({ port, portCode }: Terminal) => <DropdownItem key={portCode} header>{port}</DropdownItem>;
const terminalLinkItem = ({ id, slug, name }: Terminal) => <Link key={id} className={"dropdown-item"} to={`/${slug}`}>{name}</Link>;
const terminalLinkItems = (terminals: Terminal[]) => _(terminals).sortBy('name').map(terminalLinkItem).value();

const TerminalSelectorDropdownMenu = styled(DropdownMenu)`
    max-height: calc(100vh - 56px);
    min-height: 48px;
    overflow-y: auto;
    overflow-x: hidden;

    .dropdown-header {
        background-color: #fff;
        position: sticky;
        top: -0.5rem;
    }
`

const makeDropdownItems = (terminals: Terminal[]) => {
    const terminalsByPortCode = _(terminals).sortBy('portCode').groupBy('portCode');

    if (terminalsByPortCode.size() === 1 || terminalsByPortCode.map('length').max() as number < 2) {
        return terminalLinkItems(terminals);
    }

    return terminalsByPortCode.map((terminals, key, values) => <div key={key}>
        {key === findKey(values) ? null : separatorItem(key)}
        {portItem(nth(terminals, 0) as Terminal)}
        {terminalLinkItems(terminals)}
    </div>).value();
}

const TerminalSelector = ({ id }: TerminalSelectorProps): JSX.Element | null => {

    const { currentUser } = useCurrentUser();
    const terminals = useMemo(() => filter(currentUser?.terminals, 'active'), [currentUser]);

    const [dropdownOpen, setDropdownOpen] = useState(false as boolean);

    const history = useHistory();
    const location = useLocation();

    const toggle = useCallback((): void => setDropdownOpen(prevState => !prevState), [setDropdownOpen]);

    useEffect(() => {
        setDropdownOpen(false);
    }, [id]);

    //const selectedId = useMemo(() => id ?? (size(terminals) === 1) ? nth(terminals, 0)?.id ?? null : null, [id, terminals]);

    const [selectedTerminal, selectableTerminals] = useMemo(() =>
        id === null
            ? [size(terminals) === 1 ? nth(terminals, 0) ?? null : null, terminals]
            : reduce(terminals, ([selectedTerminal, selectableTerminals], terminal) =>
                (terminal?.id === id)
                    ? [terminal, selectableTerminals]
                    : [selectedTerminal, concat(selectableTerminals, terminal)]
                , [null, []] as [ItemType<typeof terminals> | null, typeof terminals]),
        [id, terminals])

    const redirect = useMemo(() =>
        (id === null && selectedTerminal !== null) ? `/${selectedTerminal?.slug}` : null,
        [selectedTerminal, id]);

    const dropdown = useMemo(() => size(selectableTerminals) < 1
        ? null
        : <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle color="link" className="my-2 my-sm-0 ml-1" caret>Terminal </DropdownToggle><TerminalSelectorDropdownMenu>{makeDropdownItems(selectableTerminals)}</TerminalSelectorDropdownMenu>
        </Dropdown>,
        [selectableTerminals, dropdownOpen, toggle]);

    const terminalHomeLink = useMemo(() => {
        if (selectedTerminal === null) return null;
        return <div className="mr-sm-2 form-control text-nowrap"><Link to={`/${selectedTerminal.slug}`}>{selectedTerminal.name}</Link></div>
    }, [selectedTerminal]);

    //console.log({ redirect, selectedTerminal, selectableTerminals });

    useEffect(() => {
        if (redirect !== null && location.pathname === '/') {
            history.push(redirect)
        }
    }, [history, redirect, location.pathname])

    if (terminalHomeLink === null && dropdown === null) {
        return null
    }

    return <div className='d-flex'>
        {dropdown}
        {terminalHomeLink}
    </div>;
};

export default TerminalSelector;
